import type { LocalizedString } from '../localized-string';
import type { BaseDocument } from '../mongo/base-document.interface';

/**
 * What kind of problem an issue records (SWOOP-255). There is deliberately no severity: every issue
 * has the same resolution target (see `ISSUE_TARGET_HOURS`), and the epic's per-severity targets
 * were dropped by product.
 */
export const ISSUE_TYPE = {
  FAULT: 'FAULT',
  BREAKDOWN: 'BREAKDOWN',
  RECALL: 'RECALL',
  DAMAGE: 'DAMAGE',
} as const;

export type IssueType = (typeof ISSUE_TYPE)[keyof typeof ISSUE_TYPE];

/** Insertion order is the order of the type buttons and of the type filter. */
export const ISSUE_TYPES = Object.values(ISSUE_TYPE);

/** Open → In progress → Resolved, with Reopen back to In progress (see `ISSUE_TRANSITIONS`). */
export const ISSUE_STATUS = {
  OPEN: 'OPEN',
  IN_PROGRESS: 'IN_PROGRESS',
  RESOLVED: 'RESOLVED',
} as const;

export type IssueStatus = (typeof ISSUE_STATUS)[keyof typeof ISSUE_STATUS];

export const ISSUE_STATUSES = Object.values(ISSUE_STATUS);

/**
 * One kind of event on an issue's timeline. `REPORTED`, `STATUS_CHANGED` and `COMMENT` are written by
 * the Issues screen (SWOOP-255). The three work-order kinds are reserved here so the timeline already
 * knows them; they are written by the work order flow (SWOOP-305 opens and links, SWOOP-250 closes).
 */
export const ISSUE_TIMELINE_KIND = {
  REPORTED: 'REPORTED',
  CREATED_WITH_WORK_ORDER: 'CREATED_WITH_WORK_ORDER',
  STATUS_CHANGED: 'STATUS_CHANGED',
  COMMENT: 'COMMENT',
  WORK_ORDER_OPENED: 'WORK_ORDER_OPENED',
  RESOLVED_BY_WORK_ORDER: 'RESOLVED_BY_WORK_ORDER',
} as const;

export type IssueTimelineKind = (typeof ISSUE_TIMELINE_KIND)[keyof typeof ISSUE_TIMELINE_KIND];

export const ISSUE_TIMELINE_KINDS = Object.values(ISSUE_TIMELINE_KIND);

/**
 * A fault, breakdown, recall or damage reported against one vehicle (SWOOP-255).
 *
 * Location scope follows the VEHICLE at read time: the issue stores no territory or location, so an
 * issue moves with its vehicle. Issues are never deleted (`deletedAt` stays null).
 */
export interface Issue extends BaseDocument {
  /** Tenant-unique, from the `issue` sequence. Shown as `ISS-{number}` (`formatIssueNumber`). */
  number: number;
  vehicleId: string;
  title: string;
  type: IssueType;
  status: IssueStatus;
  /** Today → the moment it was reported; an earlier day → the start of that day (business time zone). */
  reportedAt: Date;
  reporterId: string;
  /** Odometer entered when reporting; when set it was also saved as a vehicle reading. */
  odometerKm?: number | null;
  description?: string | null;
  /** Set when the issue is resolved, cleared when it is reopened. */
  resolvedAt?: Date | null;
  /** Required to resolve by hand, cleared on reopen (the timeline keeps it). */
  resolutionNote?: string | null;
  /**
   * One issue, one work order (SWOOP-305): set once and not replaced while the work order lives. A
   * cancelled work order unlinks (clears this and `workOrderNumber`) so the issue can take a new one
   * (SWOOP-250, Amir); the next work order may then set it again.
   */
  workOrderId?: string | null;
  /** Display copy of the linked work order's number, e.g. `WO-31082547`. */
  workOrderNumber?: string | null;
}

/** The fields the issue service writes on create. Base document fields are added by the repository. */
export type CreateIssue = Omit<Issue, keyof BaseDocument>;

/** One event on an issue's timeline. Append-only: never edited or deleted. */
export interface IssueTimelineEntry extends BaseDocument {
  issueId: string;
  kind: IssueTimelineKind;
  actorId: string;
  at: Date;
  fromStatus?: IssueStatus | null;
  toStatus?: IssueStatus | null;
  /** Resolution note, on a change to RESOLVED. */
  note?: string | null;
  /** Comment text, on a COMMENT entry. */
  text?: string | null;
  /** The reported type, on a REPORTED entry. */
  issueType?: IssueType | null;
  workOrderId?: string | null;
  workOrderNumber?: string | null;
}

export type CreateIssueTimelineEntry = Omit<IssueTimelineEntry, keyof BaseDocument>;

/**
 * The vehicle as shown on an issue row and in the issue panel. The client builds "Make Model Year" (e.g.
 * `Toyota Corolla 2022`) in the reader's language from the localized make label. Every field but `id` is
 * `null` when the vehicle no longer resolves, and the client shows its own "—".
 */
export interface IssueVehicleSummary {
  id: string;
  /** The vehicle's business ID (e.g. `1042`), not its database id. */
  vehicleId: string | null;
  licensePlate: string | null;
  makeName: LocalizedString | null;
  model: string | null;
  year: number | null;
}

export interface IssuePersonSummary {
  id: string;
  /** The person's full name; `null` when the user no longer resolves (the client shows "—"). */
  name: string | null;
}

/** One row of the Issues list. */
export interface IssueListItem {
  id: string;
  number: number;
  /** `ISS-{number}`. */
  displayNumber: string;
  title: string;
  type: IssueType;
  status: IssueStatus;
  reportedAt: Date;
  resolvedAt?: Date | null;
  reporter: IssuePersonSummary;
  vehicle: IssueVehicleSummary;
  workOrderId?: string | null;
  workOrderNumber?: string | null;
}

/** One timeline entry as shown in the issue panel, with its actor's name resolved. */
export interface IssueTimelineEntryView {
  id: string;
  kind: IssueTimelineKind;
  at: Date;
  actor: IssuePersonSummary;
  fromStatus?: IssueStatus | null;
  toStatus?: IssueStatus | null;
  note?: string | null;
  text?: string | null;
  issueType?: IssueType | null;
  workOrderNumber?: string | null;
}

/** Everything the issue panel shows. Photos are read through the documents API (owner `issue`). */
export interface IssueDetail extends IssueListItem {
  odometerKm?: number | null;
  description?: string | null;
  resolutionNote?: string | null;
  /** Newest first. */
  timeline: IssueTimelineEntryView[];
}

/**
 * The Issues screen's counters. `kpis` follow the selected scope only; `tabs` also follow the search
 * and the type filter. The sidebar badge reads `kpis.open` for the selected scope, so it always equals
 * the Open KPI.
 */
export interface IssueStats {
  kpis: {
    open: number;
    inProgress: number;
    /** Not resolved and older than the target. */
    pastTarget: number;
    /** Resolved in the current calendar month, in the business time zone. */
    resolvedThisMonth: number;
  };
  tabs: {
    all: number;
    open: number;
    inProgress: number;
    resolved: number;
  };
}

/** Criteria for the Issues list and stats. `territoryId` / `locationId` narrow the caller's own scope. */
export interface SearchIssuesCriteria {
  status?: IssueStatus;
  type?: IssueType;
  /** Matches the title, the issue number (`ISS-12` or `12`) and the vehicle's plate, ID or name. */
  search?: string;
  territoryId?: string;
  locationId?: string;
  /** One vehicle's issues (SWOOP-336, vehicle profile), intersected with the caller's visible vehicles. */
  vehicleId?: string;
}

export interface ReportIssueBody {
  vehicleId: string;
  title: string;
  type: IssueType;
  /** A calendar day; it may not be after today. */
  reportedAt: string;
  /** Leave out when not entered: an empty field never saves a reading. */
  odometerKm?: number;
  description?: string;
}

export interface ResolveIssueBody {
  note: string;
}

export interface CommentIssueBody {
  text: string;
}
